import { DataType, Form, FormOptions, Section, useNotify } from '@globaleris/ui.library'
import { Button, Card, Grid, makeStyles, useTheme } from '@material-ui/core'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { CUPSModel, MeterModel, UseModel } from '../../../api/Argentia/model/contracts'
import { EnumTipoCaudal } from '../../../api/oData/enums/enumTipoCaudal'
import { EnumTipoCorrector } from '../../../api/oData/enums/enumTipoCorrector'
import { EnumTipoMedidor } from '../../../api/oData/enums/enumTipoMedidor'
import Page from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { enumToSelectOptionArray } from '../../../helpers'
import { startDateIsBeforeEndDate } from '../../../helpers/formValidationsHelper'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { NonAuthRoutes } from '../../../model'
import { getCustomerDomain } from '../../../redux'
import { mapMeterRequest } from '../../meters/meters.mapper'
import { createMeter } from '../../meters/meters.service'
import { UsesCard } from '../../uses/components/usesCard/usesCard'
import { UsesTable } from '../../uses/components/usesTable/usesTable'
import { styles } from '../cups.styles'

export interface MeterAddPageProps {
  location?: {
    state: {
      cupsData?: CUPSModel
    }
  }
}

export const MeterAddPage: React.FC<MeterAddPageProps> = props => {
  const [meterData, setMeterData] = useState<MeterModel>({
    id: undefined,
    uses: [] as UseModel[],
    rented: false
  } as MeterModel)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  // BUG 77181 - PorcentajeAcumuladoSumaSinFiltro: desactivado hasta migrar validación al API con filtro por rango de fechas (era: true)
  const [showUsePercentageError, SetShowUsePercentageError] = useState<boolean>(false)
  const [meterHasOneUseLeast, setMeterHasOneUseLeast] = useState<boolean>(false)
  const [dateValidationError, setDateValidationError] = useState<boolean>(false)

  const [t] = useTranslation(['globalerisLibrary', 'cups', 'enums'])
  const history = useHistory()
  const { info, error } = useNotify()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const cupsData: CUPSModel =
    props?.location?.state?.cupsData && props?.location?.state?.cupsData[0]
  const commodity = cupsData && cupsData.commodity

  // #region Handlers Starts
  const handleSaveAndRedirect = async (data: MeterModel) => {
    setIsLoading(true)
    if (missingFieldsFormWithRequiredText(addFormSections, data)) {
      error(t('cups:components.meterAdd.forms.add.save.incompleteForm'))
    } else {
      if (cupsData) {
        await createMeter(mapMeterRequest(data), cupsData.id).then(async response => {
          setIsLoading(false)
          if (response.success) {
            info(t('cups:components.meterAdd.forms.add.save.success'))
            history.push(NonAuthRoutes.CupsDetails, { CUPSCode: cupsData.code })
          } else {
            error(response.error?.message ?? t('cups:components.meterAdd.forms.add.save.error'))
          }
        })
      } else {
        setIsLoading(false)
      }
    }
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  const options: FormOptions = {
    elevation: 1,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('cups:components.meterAdd.forms.add.analytics')
    }
  }

  const addFormSections: Section[] = [
    {
      title: t('cups:components.meterAdd.forms.add.title'),
      inputs: [
        {
          name: 'code',
          label: t('cups:components.meterAdd.forms.add.inputs.code'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'description',
          label: t('cups:components.meterAdd.forms.add.inputs.description'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'um',
          label: t('cups:components.meterAdd.forms.add.inputs.um'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'rented',
          label: t('cups:components.meterAdd.forms.add.inputs.rented'),
          type: DataType.SWITCH,
          defaultValue: false,
          required: true
        },
        {
          name: 'meterType',
          label: t('cups:components.meterAdd.forms.add.inputs.meterType'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumTipoMedidor, 'enums:EnumTipoMedidor'),
          clearable: true
        },
        {
          name: 'correctorType',
          label: t('cups:components.meterAdd.forms.add.inputs.correctorType'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumTipoCorrector, 'enums:EnumTipoCorrector'),
          clearable: true
        },
        {
          name: 'flowType',
          label: t('cups:components.meterAdd.forms.add.inputs.flowType'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumTipoCaudal, 'enums:EnumTipoCaudal'),
          clearable: true
        },
        {
          name: 'weight',
          label: t('cups:components.meterAdd.forms.add.inputs.weight'),
          type: DataType.NUMBER,
          placeholder: '100%',
          suffix: '%'
        },
        {
          name: 'startDate',
          label: t('cups:components.meterAdd.forms.add.inputs.startDate'),
          type: DataType.DATE,
          required: true,
          errorState: dateValidationError,
          errorText: t('cups:components.meterAdd.forms.add.validations.startDateIsBeforeEndDate'),
          onChange: (value, formState) => {
            const formData = formState as MeterModel
            setDateValidationError(!startDateIsBeforeEndDate(formData.startDate, formData.endDate))
          }
        },
        {
          name: 'endDate',
          label: t('cups:components.meterAdd.forms.add.inputs.endDate'),
          type: DataType.DATE,
          errorState: dateValidationError,
          clearable: true,
          onChange: (value, formState) => {
            const formData = formState as MeterModel
            setDateValidationError(!startDateIsBeforeEndDate(formData.startDate, formData.endDate))
          }
        }
      ]
    }
  ]

  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  // #region future components

  const handleStateUpdate = async (rowData: UseModel[]) => {
    // BUG 77181 - PorcentajeAcumuladoSumaSinFiltro: desactivado hasta migrar validación al API con filtro por rango de fechas
    // if (!usesPercentageSums100(rowData)) SetShowUsePercentageError(true)
    // else SetShowUsePercentageError(false)

    if (meterHasUses(rowData)) setMeterHasOneUseLeast(true)
    else setMeterHasOneUseLeast(false)

    setMeterData(prevState => ({
      ...prevState,
      uses: rowData
    }))
  }

  // BUG 77181 - PorcentajeAcumuladoSumaSinFiltro: desactivado hasta migrar validación al API con filtro por rango de fechas
  // const usesPercentageSums100 = (rowData: UseModel[]): boolean => {
  //   const totalPercentage = rowData.reduce((total, use) => total + (use.percentage || 0), 0)
  //   return totalPercentage === 1
  // }

  const meterHasUses = (rowData: UseModel[]): boolean => {
    return rowData.length > 0
  }
  // #endregion future components

  return (
    <Page>
      <PageHeader title={t('cups:components.meterAdd.title')}>
        <Grid container direction="row" justify="flex-end" spacing={2}>
          <Grid item>
            <Button
              size="small"
              variant="text"
              color="primary"
              // disabled={isFetching}
              onClick={() => {
                history.goBack()
              }}
            >
              {t('cups:components.meterAdd.forms.add.buttons.cancel')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={showUsePercentageError || !meterHasOneUseLeast || dateValidationError}
              onClick={() => {
                handleSaveAndRedirect(meterData)
              }}
            >
              {t('cups:components.meterAdd.forms.add.buttons.save')}
            </Button>
          </Grid>
        </Grid>
      </PageHeader>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card raised className={classes.card}>
            <Form sections={addFormSections} data={meterData} options={options} />
          </Card>
        </Grid>
        <Grid item xs={12}>
          <UsesCard showPercentageError={showUsePercentageError} hasOneUse={meterHasOneUseLeast}>
            <UsesTable
              listUsesData={meterData.uses}
              handleStateUpdate={handleStateUpdate}
              commodity={commodity}
              customCaption={t('cups:components.meterAdd.customCardCaption')}
            />
          </UsesCard>
        </Grid>
      </Grid>
    </Page>
  )
}
